<script>
import gql from "graphql-tag";
import BuyContainers from "./BuyContainers";
import BuyTimeline from "./BuyTimeline";
import BuyDialogOfferForm from "./BuyDialogOfferForm";
import { injectActiveEmployee } from "@/mixins/employee";
import { hasSnackbarAccess } from "@/mixins/ui";
import BuyDialogOfferSummary from "./BuyDialogOfferSummary";

import { differenceInMinutes, parseISO } from "date-fns";

const BUY_DIALOG_OFFER = gql`
  query BUY_DIALOG_OFFER($id: ID!) {
    buy(id: $id) {
      id
      status
      quoteItems
      quoteAmount
      internalNote
      sorterId
      takeReasons
      passReasons
      completedAt
      estimatedPickupAt
    }
  }
`;

const BUY_DIALOG_META = gql`
  query BUY_DIALOG_META($id: ID!) {
    buy(id: $id) {
      id
      containerNum
      containerDescription
      signatureFileName
      signatureUrl
      estimatedPickupAt
      checkedInAt
      checkedInByEmployeeId
      status
      transId
      buyId: id
      sorterId
      quoteItems
    }
  }
`;

const BUY_DIALOG_CUSTOMER = gql`
  query BUY_DIALOG_CUSTOMER($id: ID!, $storeId: ID!, $buyId: ID!) {
    customer(id: $id, storeId: $storeId) {
      id
      firstName
      lastName
      phoneNumber
      email
      flagged
      flagReason
      customerId: id
      driverLicense {
        id
        address1
        address2
        city
        state
        postalCode
      }
    }
    buy(id: $buyId) {
      id
      status
      licenseVerifiedAt
    }
  }
`;

export default {
  name: "BuyDialogCompleteContent",
  inject: ["registerBuyDialogForm"],
  components: {
    BuyContainers,
    BuyTimeline,
    BuyDialogOfferForm,
    BuyDialogOfferSummary,
  },
  mixins: [hasSnackbarAccess, injectActiveEmployee],
  props: {
    customerId: {
      type: String,
      default: undefined,
    },
    buyStatus: {
      type: String,
      default: undefined,
    },
    buyId: {
      type: String,
      default: undefined,
    },
    storeId: {
      type: [String, Number],
      default: undefined,
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingSorter: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isPastDue() {
      if (!this.buyOfferData) {
        return false;
      }

      const minutesDiff = differenceInMinutes(
        parseISO(this.buyOfferData.completedAt),
        parseISO(this.buyOfferData.estimatedPickupAt)
      );

      // If completed more than 10 mins after due, display the Past due info label
      return minutesDiff >= 10;
    },
    isPastDueByMuch() {
      if (!this.buyOfferData) {
        return false;
      }

      const minutesDiff = differenceInMinutes(
        parseISO(this.buyOfferData.completedAt),
        parseISO(this.buyOfferData.estimatedPickupAt)
      );

      // If completed more than 30 mins after due, display the Past due info label in red!
      return minutesDiff >= 30;
    },
  },
  apollo: {
    buyMetaData: {
      loadingKey: "buyMetaLoading",
      query: BUY_DIALOG_META,
      variables() {
        return {
          id: this.buyId,
        };
      },
      skip() {
        return !this.buyId;
      },
      fetchPolicy: "cache-and-network",
      update(response) {
        return response.buy;
      },
    },
    customerBuyData: {
      loadingKey: "customerBuyLoading",
      query: BUY_DIALOG_CUSTOMER,
      variables() {
        return {
          id: this.customerId,
          storeId: this.storeId,
          buyId: this.buyId,
        };
      },
      skip() {
        return !this.customerId;
      },
      fetchPolicy: "cache-and-network",
      update(response) {
        return {
          customer: response.customer,
          buy: response.buy,
        };
      },
    },
    buyOfferData: {
      loadingKey: "offerDataLoading",
      query: BUY_DIALOG_OFFER,
      variables() {
        return {
          id: this.buyId,
        };
      },
      skip() {
        return !this.buyId;
      },
      fetchPolicy: "cache-and-network",
      update(response) {
        return response.buy;
      },
    },
  },
};
</script>

<template>
  <div>
    <div v-if="buyMetaData">
      <div class="d-flex align-center">
        <BuyContainers
          :number="buyMetaData.containerNum"
          :description="buyMetaData.containerDescription"
        />
        <div
          v-if="isPastDue"
          :class="{
            caption: true,
            'ml-3': true,
            'error--text font-weight-bold': isPastDueByMuch,
          }"
        >
          PAST DUE
        </div>
      </div>

      <div class="mt-sm-6 mt-3">
        <div class="mr-2 d-sm-flex align-center justify-start">
          <div
            v-if="buyOfferData && buyOfferData.quoteItems"
            class="sorter-and-items"
          >
            We would like to offer
            <span class="font-weight-bold">{{
              $filters.dollars(buyOfferData.quoteAmount)
            }}</span>
            for
            <span class="font-weight-bold">{{
              buyOfferData.quoteItems || 0
            }}</span>
            items
          </div>
          <div v-else class="sorter-and-items text-subtitle-2">
            No items have been entered into DRS
          </div>
        </div>
      </div>

      <BuyDialogOfferSummary
        v-if="buyOfferData && !showEdit"
        :show-signature-field="false"
        class="mt-sm-6 mt-3"
        v-bind="{ ...buyOfferData, buyId: buyId, storeId: storeId }"
        @loading="(v) => $emit('loading', v)"
      />
      <BuyDialogOfferForm
        v-if="buyOfferData && showEdit"
        :show-signature-field="false"
        class="mt-sm-6 mt-3"
        v-bind="{ ...buyOfferData, buyId: buyId, storeId: storeId }"
        @loading="(v) => $emit('loading', v)"
      />

      <div v-if="isSmallScreen">
        <v-divider class="mb-3 timeline-divider" />
        <BuyTimeline
          :buy-id="buyId"
          :class="{ 'mt-sm-6 mt-3': !buyMetaData.sorterId }"
        />
      </div>
    </div>
  </div>
</template>

<style scope lang="scss">
.sorter-and-items {
  flex-basis: 50%;
}

.timeline-divider {
  margin-left: -24px;
  max-width: none;
  width: calc(100% + 48px);
}
</style>
